import { ArrowIcon, LinkButton, Text } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"

// The taxonomy banner: one photograph running edge to edge, with the headline set
// in ink on its clean side. Klarna's payments page is the model — full bleed, a
// three-to-five-word headline, one sentence under it, one link, and nothing else on
// the picture. No duotone, no scrim, no price, no product card: a banner states what
// the category is, and the shelves below it do the selling.

interface CategoryCoverProps {
  eyebrow?: string
  title: string
  accent?: string
  sub?: string
  cta?: string
  ctaHref?: string
  image: string
  imageCrop?: ImageCrop
  imageAlt?: string
  priority?: boolean
  headingLevel?: "h1" | "h2"
  wash?: boolean
}

export const CategoryCover = ({ eyebrow, title, accent, sub, cta, ctaHref, image, imageCrop, imageAlt, priority, headingLevel = "h2", wash = true }: CategoryCoverProps) => {
  const Heading = headingLevel
  return (
    <div className="@container relative">
      <div className="bg-surface-muted @5xl:h-hero-stage @3xl:aspect-photo relative isolate aspect-4/5 overflow-hidden @5xl:aspect-auto">
        <TileImage src={image} crop={imageCrop} alt={imageAlt ?? title} fill sizes="100vw" priority={priority} className="object-cover object-[74%_50%] @5xl:object-[68%_50%]" />
        {wash && <span aria-hidden className="bg-wash-inset pointer-events-none absolute inset-0 hidden @5xl:block" />}
        <CoverLink href={cta ? ctaHref : undefined} />
      </div>
      {/* A separate positioner because the photograph bleeds and the copy must not:
          this one holds the page column the sections below it start on. */}
      <div className="max-w-page mx-auto @5xl:pointer-events-none @5xl:absolute @5xl:inset-0">
        {/* One copy block, moved rather than duplicated: a second responsive copy
            leaves both in the accessibility tree, so the headline is announced twice
            and the page ships duplicate content no matter which one is painted. */}
        <div className="px-gut @5xl:max-w-hero-inset @5xl:px-hero-inset flex flex-col items-center pt-800 text-center @5xl:absolute @5xl:inset-0 @5xl:items-start @5xl:justify-center @5xl:pt-0 @5xl:text-left">
          {eyebrow && (
            <Text as="span" variant="caption" family="display" tone="inverted" className="bg-fg-strong mb-350 inline-flex h-7 w-fit items-center rounded-full px-300 font-bold">
              {eyebrow}
            </Text>
          )}
          <Heading className="font-display text-section text-fg-strong @5xl:text-fg-on-paper text-balance">
            {title}
            {accent && <span className="text-brand block">{accent}</span>}
          </Heading>
          {sub && (
            <Text as="p" variant="body" family="text" tone="default" className="@5xl:text-fg-on-paper @3xl:text-heading-md mt-500 max-w-110 font-normal @5xl:max-w-none">
              {sub}
            </Text>
          )}
          {cta && (
            <div className="mt-800 flex w-full @5xl:w-auto">
              <LinkButton href={ctaHref} variant="primary" size="md" fullWidth iconRight={<ArrowIcon size={18} />} className="pointer-events-auto font-semibold @5xl:w-auto">
                {cta}
              </LinkButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
